import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {Typography} from '@mui/material';
import Box from '@mui/material/Box';
import TechnicalMapJobCard from './TechnicalMapJobCard';
import TechnicalMapJobCardGroup from './TechnicalMapJobCardGroup';
import TechnicalMapTabletPagination from './TechnicalMapTabletPagination';
import TechnicalMapTabletToolbar from './TechnicalMapTabletToolbar';
import TechnicalMapTabletFiltersPanel from './TechnicalMapTabletFiltersPanel';
import {groupJobsByType} from './groupJobsByType';
import {technicalMapMobileMq} from './technicalMapBreakpoints';

const TechnicalMapJobCardList = ({
                                     data,
                                     groups: groupsFromTable,
                                     isGrouped,
                                     onGroupingChange,
                                     globalFilter,
                                     onGlobalFilterChange,
                                     columnFilters,
                                     onColumnFiltersChange,
                                     sorting,
                                     onSortingChange,
                                     sortingKey,
                                     setData,
                                     handleModalAlert,
                                     tableOptions,
                                     tableOptionsHandle,
                                     isSettingsRole,
                                     isActionActive,
                                     onExecuteJob,
                                     onCancelExecuteJob,
                                     onDeleteJob,
                                 }) => {
    const [isFiltersOpen, setIsFiltersOpen] = useState(false);
    const [pageIndex, setPageIndex] = useState(0);
    const [expandAllRevision, setExpandAllRevision] = useState(0);
    const [allExpanded, setAllExpanded] = useState(true);
    const [expandedWorkersByJobId, setExpandedWorkersByJobId] = useState({});
    const pageSize = tableOptions?.pageSize ?? 50;
    const jobs = Array.isArray(data) ? data : [];
    const jobsKey = jobs.map((job) => job.id).join(',');
    const showFiltersOnly = isFiltersOpen;

    const groups = useMemo(() => {
        if (isGrouped && Array.isArray(groupsFromTable)) {
            return groupsFromTable.filter((group) => group.jobs.length > 0);
        }
        return groupJobsByType(jobs);
    }, [groupsFromTable, isGrouped, jobs]);

    const pageItemsCount = isGrouped ? groups.length : jobs.length;
    const totalJobsCount = isGrouped
        ? groups.reduce((sum, group) => sum + group.jobs.length, 0)
        : jobs.length;

    useEffect(() => {
        setPageIndex(0);
    }, [jobsKey, isGrouped, sortingKey, globalFilter, columnFilters]);

    useEffect(() => {
        const jobIds = new Set(jobs.map((job) => String(job.id)));
        setExpandedWorkersByJobId((prev) => {
            const nextEntries = Object.entries(prev).filter(([jobId]) => jobIds.has(String(jobId)));
            if (nextEntries.length === Object.keys(prev).length) {
                return prev;
            }
            return Object.fromEntries(nextEntries);
        });
    }, [jobsKey]);

    const safePageIndex = useMemo(() => {
        if (pageItemsCount === 0) {
            return 0;
        }
        const maxPage = Math.ceil(pageItemsCount / pageSize) - 1;
        return Math.min(pageIndex, Math.max(0, maxPage));
    }, [pageItemsCount, pageIndex, pageSize]);

    const paginatedGroups = useMemo(() => {
        const start = safePageIndex * pageSize;
        return groups.slice(start, start + pageSize);
    }, [groups, pageSize, safePageIndex]);

    const paginatedJobs = useMemo(() => {
        const start = safePageIndex * pageSize;
        return jobs.slice(start, start + pageSize);
    }, [jobs, pageSize, safePageIndex]);

    const handleToggleExpandAll = () => {
        setAllExpanded((prev) => !prev);
        setExpandAllRevision((prev) => prev + 1);
    };

    const expandAllProps = {
        expandAll: allExpanded,
        expandAllRevision,
    };

    const handlePageChange = (newPage) => {
        setPageIndex(newPage);
    };

    const handleRowsPerPageChange = (event) => {
        const nextPageSize = Number(event.target.value);
        tableOptionsHandle({
            ...tableOptions,
            pageSize: nextPageSize,
            pageIndex: 0,
        });
        setPageIndex(0);
    };

    const handleToggleFilters = () => {
        setIsFiltersOpen((prev) => !prev);
    };

    const handleSearchChange = (event) => {
        onGlobalFilterChange(event.target.value);
        setPageIndex(0);
    };

    const handleClearSearch = () => {
        onGlobalFilterChange('');
        setPageIndex(0);
    };

    const handleResetFilters = () => {
        onColumnFiltersChange([]);
        onSortingChange([]);
        tableOptionsHandle({
            ...tableOptions,
            pageIndex: 0,
        });
        setPageIndex(0);
        setIsFiltersOpen(false);
    };

    const handleApplyFilters = ({columnFilters: nextColumnFilters, sorting: nextSorting}) => {
        onColumnFiltersChange(nextColumnFilters);
        onSortingChange(nextSorting);
        tableOptionsHandle({
            ...tableOptions,
            pageIndex: 0,
        });
        setPageIndex(0);
        setIsFiltersOpen(false);
    };

    const setWorkersExpanded = useCallback((jobId, expanded) => {
        const key = String(jobId);
        setExpandedWorkersByJobId((prev) => {
            if (Boolean(prev[key]) === expanded) {
                return prev;
            }
            if (!expanded) {
                const {[key]: removed, ...rest} = prev;
                return rest;
            }
            return {...prev, [key]: true};
        });
    }, []);

    const toggleWorkersExpanded = useCallback((jobId) => {
        const key = String(jobId);
        setExpandedWorkersByJobId((prev) => {
            if (prev[key]) {
                const {[key]: removed, ...rest} = prev;
                return rest;
            }
            return {...prev, [key]: true};
        });
    }, []);

    const cardProps = useMemo(() => ({
        setData,
        handleModalAlert,
        isSettingsRole,
        isActionActive,
        onExecuteJob,
        onCancelExecuteJob,
        onDeleteJob,
        expandedWorkersByJobId,
        setWorkersExpanded,
        toggleWorkersExpanded,
    }), [
        expandedWorkersByJobId,
        handleModalAlert,
        isActionActive,
        isSettingsRole,
        onCancelExecuteJob,
        onDeleteJob,
        onExecuteJob,
        setData,
        setWorkersExpanded,
        toggleWorkersExpanded,
    ]);

    return (
        <Box>
            <TechnicalMapTabletToolbar
                isFiltersOpen={isFiltersOpen}
                onToggleFilters={handleToggleFilters}
                isGrouped={isGrouped}
                onGroupingChange={onGroupingChange}
                allExpanded={allExpanded}
                onToggleExpandAll={handleToggleExpandAll}
                expandControlsDisabled={pageItemsCount === 0}
                search={globalFilter}
                onSearchChange={handleSearchChange}
                onClearSearch={handleClearSearch}
            />

            {isFiltersOpen && (
                <TechnicalMapTabletFiltersPanel
                    isOpen={isFiltersOpen}
                    columnFilters={columnFilters}
                    sorting={sorting}
                    onApply={handleApplyFilters}
                    onReset={handleResetFilters}
                />
            )}

            {!showFiltersOnly && (
                <>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 2,
                            px: '2px',
                            pb: 1,
                            [technicalMapMobileMq]: {
                                gap: 1,
                            },
                        }}
                    >
                        {isGrouped ? (
                            paginatedGroups.length === 0 ? (
                                <Typography sx={{textAlign: 'center', py: 4, color: 'text.secondary'}}>
                                    Нет данных
                                </Typography>
                            ) : (
                                paginatedGroups.map((group) => (
                                    <TechnicalMapJobCardGroup
                                        key={group.typeJobName}
                                        typeJobName={group.typeJobName}
                                        jobs={group.jobs}
                                        {...expandAllProps}
                                        {...cardProps}
                                    />
                                ))
                            )
                        ) : (
                            paginatedJobs.length === 0 ? (
                                <Typography sx={{textAlign: 'center', py: 4, color: 'text.secondary'}}>
                                    Нет данных
                                </Typography>
                            ) : (
                                paginatedJobs.map((job) => (
                                    <TechnicalMapJobCard
                                        key={job.id}
                                        job={job}
                                        workersExpanded={Boolean(expandedWorkersByJobId[String(job.id)])}
                                        toggleWorkersExpanded={toggleWorkersExpanded}
                                        setWorkersExpanded={setWorkersExpanded}
                                        {...expandAllProps}
                                        setData={setData}
                                        handleModalAlert={handleModalAlert}
                                        isSettingsRole={isSettingsRole}
                                        isActionActive={isActionActive}
                                        onExecuteJob={onExecuteJob}
                                        onCancelExecuteJob={onCancelExecuteJob}
                                        onDeleteJob={onDeleteJob}
                                    />
                                ))
                            )
                        )}
                    </Box>

                    <TechnicalMapTabletPagination
                        count={pageItemsCount}
                        page={safePageIndex}
                        onPageChange={handlePageChange}
                        rowsPerPage={pageSize}
                        onRowsPerPageChange={handleRowsPerPageChange}
                        isGrouped={isGrouped}
                        totalJobsCount={totalJobsCount}
                    />
                </>
            )}
        </Box>
    );
};

export default TechnicalMapJobCardList;
